import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import useShots from '../../contexts/shots/provider';
import { AUTOPLAY_SECOND_BUFFER } from '../../hooks/use-shot-utilities';
import { UpdateScheduledStreamStatus } from '../../connection/scheduled-streams';
import { ScheduledStreamStatus } from '../../stores/types/connection';
import { OpenTokContext } from '../../contexts/opentok/opentok-context';

const showMetrics = new URLSearchParams(window.location.search).get('metrics') === 'true';

export default function EmbedController() {
	const user = useSelector((state: GS) => state.SessionReducer.user);
	const embed = useSelector((state: GS) => state.SessionReducer.embed);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);
	const shots = useSelector((state: GS) => state.ShotsReducer.shots);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);

	const { currentShot, lockAutoPlay } = useShots();

	const {
		shot_id: shotId,
		shot_data: {
			autoplay: autoPlay,
		},
	} = currentShot;

	const { opentok } = useContext(OpenTokContext);

	const [showEnded, setShowEnded] = React.useState<boolean>(false);
	const lastShotTimmy = React.useRef<NodeJS.Timeout | null>(null);

	const [totalBandwidth, setTotalBandwidth] = useState(0);
	const prevBytesRef = useRef<Map<string, { bytes: number; ts: number }>>(new Map());
	const opentokRef = useRef(opentok);
	opentokRef.current = opentok;

	const updateScheduledStreamStatus = useCallback((status: ScheduledStreamStatus) => {
		if (
			!token
			|| !meeting?.meeting
			|| !scheduledStream
		) {
			return;
		}

		if (scheduledStream.id && scheduledStream.startTime) {
			UpdateScheduledStreamStatus(
				token,
				meeting.meeting,
				scheduledStream.id,
				1,
				status,
			);
		}
	}, [scheduledStream, meeting, user, embed]);

	const isLastShot = useMemo(() => {
		const currShotIsLastShot = shotId === shots[shots.length - 1].shot_id;
		if (!currShotIsLastShot && showEnded) {
			setShowEnded(false);
			updateScheduledStreamStatus(ScheduledStreamStatus.None);
		}
		return currShotIsLastShot;
	}, [shotId, shots, showEnded, updateScheduledStreamStatus]);

	useEffect(() => {
		const endScheduledStream = isLastShot && lockAutoPlay && autoPlay;
		if (endScheduledStream && !showEnded) {
			// after autoplay is locked, the time remaining in the shot is the buffer
			setShowEnded(true);
			lastShotTimmy.current = setTimeout(() => {
				updateScheduledStreamStatus(ScheduledStreamStatus.Ended);
			}, 1000 * AUTOPLAY_SECOND_BUFFER + 200);
		} else if (!endScheduledStream && showEnded) {
			setShowEnded(false);
			if (lastShotTimmy.current) clearTimeout(lastShotTimmy.current);
		}
	}, [
		shotId,
		autoPlay,
		shots,
		lockAutoPlay,
		isLastShot,
		showEnded,
		updateScheduledStreamStatus,
	]);

	useEffect(() => {
		if (!showMetrics) return undefined;

		const pollBandwidth = () => {
			const ot = opentokRef.current;
			if (!ot) return;

			const entries = Object.entries(ot.subscribers);
			if (entries.length === 0) return;

			let completed = 0;
			let sum = 0;

			entries.forEach(([streamId, sub]) => {
				sub.getStats((err, stats) => {
					if (!err && stats) {
						const bytes = (stats.video?.bytesReceived ?? 0)
							+ (stats.audio?.bytesReceived ?? 0);
						const { timestamp: ts } = stats;
						const prev = prevBytesRef.current.get(streamId);

						if (prev && ts > prev.ts) {
							const deltaSec = (ts - prev.ts) / 1000;
							sum += (bytes - prev.bytes) / deltaSec / (1024 * 1024);
						}

						prevBytesRef.current.set(streamId, { bytes, ts });
					}

					completed += 1;
					if (completed === entries.length) {
						setTotalBandwidth(sum);
					}
				});
			});
		};

		pollBandwidth();
		const interval = setInterval(pollBandwidth, 2000);
		return () => clearInterval(interval);
	}, []);

	if (!showMetrics) return (<></>);

	return (
		<div style={{
			position: 'fixed',
			bottom: 8,
			right: 8,
			background: 'rgba(0, 0, 0, 0.7)',
			color: '#FFD700',
			padding: '4px 8px',
			borderRadius: 4,
			fontSize: 12,
			fontFamily: 'monospace',
			zIndex: 9999,
			pointerEvents: 'none',
		}}>
			&#x2193; {totalBandwidth.toFixed(2)} MB/s
		</div>
	);
}
